import { Injectable } from '@angular/core';
import { AppVersionService as SVC, ApplicationInfo } from '../domain';
import { HttpClient } from '@angular/common/http';
import { AppSettings } from '../app-settings';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { ConsoleService } from 'app/widgets/console.service';

@Injectable()
export class AppVersionService implements SVC {
    public serverVersion: string;
    public uiVersion: string;
    public serverInfo: ApplicationInfo;
    private clockChangedSubject = new Subject<void>();
    private reviewDatesChangedSubject = new Subject<void>();

    public get effectiveNow(): Date {
        return this.simulatedDateTime ?? new Date();
    }

    public get simulatedDateTime(): Date | null {
        const simulated = this.serverInfo?.simulatedDate;
        if (!simulated) {
            return null;
        }

        const [year, month, day] = simulated.split('-').map(Number);
        if (!year || !month || !day) {
            return null;
        }

        const now = new Date();
        
        return new Date(
            year,
            month - 1,
            day,
            now.getHours(),
            now.getMinutes(),
            now.getSeconds(),
            now.getMilliseconds()
        );
    }

    public get clockChanged$(): Observable<void> {
        return this.clockChangedSubject.asObservable();
    }

    public notifyClockChanged(): void {
        this.clockChangedSubject.next();
    }

    public get reviewDatesChanged$(): Observable<void> {
        return this.reviewDatesChangedSubject.asObservable();
    }

    public notifyReviewDatesChanged(): void {
        this.reviewDatesChangedSubject.next();
    }

    constructor(
        private appSettings: AppSettings,
        private http: HttpClient,
        private console: ConsoleService,
    ) {}

    private fetchServerVersion = (): Promise<ApplicationInfo | null> =>
        firstValueFrom(this.http.get<ApplicationInfo>(this.appSettings.APIURL + '/version'))
            .catch(() => null);

    public fetchUiVersion = () => {
        const cacheBust = Date.now();
        return firstValueFrom(
            this.http.get(`/assets/version.txt?v=${cacheBust}`, {
                responseType: 'text',
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache'
                }
            })
        )
            .then(ver => ver.trim())
            .catch(() => null);
    };

    public async config(): Promise<null> {
        const [serverInfo, uiVersion] = await Promise.all([
            this.fetchServerVersion(),
            this.fetchUiVersion()
        ]);
        this.uiVersion = uiVersion;
        this.serverInfo = serverInfo ?? {};
        this.serverVersion = serverInfo?.version ? serverInfo.version.trim() : null;
        this.console.log(`Initialized AppVersionService with Server Version: '${this.serverVersion}' and UI Version: '${uiVersion}'`);
        return null;
    }
}
